import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Typography, Box, Grid,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, TextField, MenuItem, IconButton,
  CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
  Alert, Pagination, Switch, Tooltip, Badge,
} from '@mui/material';
import SearchIcon           from '@mui/icons-material/Search';
import VisibilityIcon       from '@mui/icons-material/Visibility';
import EditIcon             from '@mui/icons-material/Edit';
import BlockIcon            from '@mui/icons-material/Block';
import CheckCircleIcon      from '@mui/icons-material/CheckCircle';
import RefreshIcon          from '@mui/icons-material/Refresh';
import DirectionsCarIcon    from '@mui/icons-material/DirectionsCar';
import DeliveryDiningIcon   from '@mui/icons-material/DeliveryDining';
import PersonIcon           from '@mui/icons-material/Person';
import RestaurantIcon       from '@mui/icons-material/Restaurant';
import VerifiedUserIcon     from '@mui/icons-material/VerifiedUser';
import GroupsIcon           from '@mui/icons-material/Groups';
import CarRentalIcon        from '@mui/icons-material/CarRental';
import LocalShippingIcon    from '@mui/icons-material/LocalShipping';
import ShoppingBagIcon      from '@mui/icons-material/ShoppingBag';
import ArrowForwardIcon        from '@mui/icons-material/ArrowForward';
import TwoWheelerIcon          from '@mui/icons-material/TwoWheeler';
import CloseIcon               from '@mui/icons-material/Close';
import CampaignIcon            from '@mui/icons-material/Campaign';
import SendIcon                from '@mui/icons-material/Send';
import PeopleIcon              from '@mui/icons-material/People';
import PersonOffIcon           from '@mui/icons-material/PersonOff';
import adminService         from '../../services/admin.service';
import DeletionRequestsTab  from './DeletionRequestsTab';
import SupportChatTab      from './SupportChatTab';
import StripeTab           from './StripeTab';
import partnerService       from '../../services/partner.service';
import api                  from '../../services/api';
import { fmtDateTZ, TZ }   from '../../utils/date';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
} from 'recharts';

const FONT = "'DM Sans', system-ui, sans-serif";
const adminTheme = createTheme({
  palette: { mode: 'light', primary: { main: '#552583' }, secondary: { main: '#FDB927' } },
  typography: { fontFamily: FONT, button: { textTransform: 'none', fontWeight: 700 } },
  shape: { borderRadius: 10 },
});

/* ── Palette ─────────────────────────────────────────────────────── */
const P = {
  bg:       '#faf8fc',
  surface:  '#ffffff',
  line:     '#ece6f3',
  lineFine: '#f3eff8',
  purple:   '#552583',
  gold:     '#552583',
  green:    '#1D9E75',
  blue:     '#3b82f6',
  orange:   '#f59e0b',
  red:      '#ef4444',
  text:     '#1d1530',
  soft:     'rgba(29,21,48,0.62)',
  muted:    'rgba(29,21,48,0.45)',
};

/* shared micro-label style */
const micro = { fontSize: '0.78rem', fontWeight: 500, color: P.soft };
const monoNum = { fontWeight: 700, fontVariantNumeric: 'tabular-nums' };

/* ── Maps ────────────────────────────────────────────────────────── */
const userTypeMap = {
  client:           { label: 'Client',       color: P.blue,   icon: <PersonIcon fontSize="small" /> },
  driver:           { label: 'Chauffeur',    color: P.purple, icon: <DirectionsCarIcon fontSize="small" /> },
  delivery:         { label: 'Livreur',      color: P.orange, icon: <DeliveryDiningIcon fontSize="small" /> },
  restaurant_owner: { label: 'Restaurateur', color: P.red,    icon: <RestaurantIcon fontSize="small" /> },
  admin:            { label: 'Admin',        color: P.gold,   icon: <PersonIcon fontSize="small" /> },
};

const orderStatusMap = {
  pending:    { label: 'En attente',   color: P.orange },
  confirmed:  { label: 'Confirmée',   color: P.blue },
  preparing:  { label: 'Préparation', color: P.purple },
  ready:      { label: 'Prête',       color: P.green },
  picked_up:  { label: 'Récupérée',   color: P.purple },
  delivering: { label: 'Livraison',   color: '#06b6d4' },
  delivered:  { label: 'Livrée',      color: P.green },
  collected:  { label: 'Retirée',     color: P.green },
  scheduled:  { label: 'Programmée',  color: P.blue },
  cancelled:  { label: 'Annulée',     color: P.red },
};

const fmt    = (n) => (n || 0).toLocaleString('fr-FR');
const fmtEur = (n) => `${(parseFloat(n) || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

/* ── Thin separator ──────────────────────────────────────────────── */
const Sep = () => <Box sx={{ height: '0.5px', bgcolor: P.line, my: 0 }} />;

/* ── Stat block (replaces KpiCard) ───────────────────────────────── */
const Stat = ({ label, value, sub, accent }) => (
  <Box sx={{ bgcolor: P.surface, border: `1px solid ${P.line}`, borderRadius: '12px', p: 2, height: '100%' }}>
    <Typography sx={{ ...micro, mb: 0.75 }}>{label}</Typography>
    <Typography sx={{ ...monoNum, fontSize: '1.6rem', lineHeight: 1.1, color: accent || P.text, letterSpacing: '-0.02em' }}>
      {value}
    </Typography>
    {sub && <Typography sx={{ fontSize: '0.75rem', color: P.soft, mt: 0.75 }}>{sub}</Typography>}
  </Box>
);

/* ── Courbe des ventes (14 jours) ────────────────────────────────── */
const RevenueChart = ({ daily }) => {
  const data = (daily || []).map((d) => ({
    day: new Date(`${d.day}T12:00:00`).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }),
    revenue: Math.round(parseFloat(d.revenue || 0) * 100) / 100,
    orders: parseInt(d.orders || 0, 10),
  }));
  const total = data.reduce((s, d) => s + d.revenue, 0);
  const last7 = data.slice(-7).reduce((s, d) => s + d.revenue, 0);
  const prev7 = data.slice(0, 7).reduce((s, d) => s + d.revenue, 0);
  const trend = prev7 > 0 ? Math.round(((last7 - prev7) / prev7) * 100) : null;
  return (
    <Box sx={{ bgcolor: P.surface, border: `1px solid ${P.line}`, borderRadius: '12px', p: 2.5 }}>
      <Typography sx={micro}>Ventes · 14 derniers jours</Typography>
      <Box display="flex" alignItems="baseline" gap={1.5} sx={{ mb: 1.5 }}>
        <Typography sx={{ ...monoNum, fontSize: '1.8rem', color: P.text }}>{fmtEur(total.toFixed(2))}</Typography>
        {trend !== null && (
          <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: trend >= 0 ? P.green : P.red }}>
            {trend >= 0 ? '+' : ''}{trend} % sur 7 jours
          </Typography>
        )}
      </Box>
      <Box sx={{ height: 200 }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
            <defs>
              <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={P.purple} stopOpacity={0.18} />
                <stop offset="100%" stopColor={P.purple} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke={P.lineFine} vertical={false} />
            <XAxis dataKey="day" tick={{ fontSize: 11, fill: P.muted }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
            <YAxis tick={{ fontSize: 11, fill: P.muted }} tickLine={false} axisLine={false} width={48} />
            <RTooltip
              formatter={(v, n) => (n === 'revenue' ? [`${v.toLocaleString('fr-FR')} €`, 'Ventes'] : [v, 'Commandes'])}
              contentStyle={{ borderRadius: 8, border: `1px solid ${P.line}`, fontSize: 12 }}
            />
            <Area type="monotone" dataKey="revenue" stroke={P.purple} strokeWidth={2.5} fill="url(#rev)" />
          </AreaChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

/* ── Navigation groupée (maquette B) ─────────────────────────────── */
const NAV = [
  { label: 'Accueil',       tabs: [0] },
  { label: 'Commandes',     tabs: [2] },
  { label: 'Partenaires',   tabs: [3, 6] },
  { label: 'Utilisateurs',  tabs: [1, 8] },
  { label: 'Argent',        tabs: [10, 4, 5] },
  { label: 'Messages',      tabs: [9] },
  { label: 'Notifications', tabs: [7] },
];

/* ── Table header cell style ─────────────────────────────────────── */
const thSx = {
  borderBottom: `1px solid ${P.line}`, color: P.soft, fontWeight: 700,
  fontSize: '0.75rem', py: 1.5, px: 2, bgcolor: P.bg,
};

const tdSx = { borderBottom: `0.5px solid ${P.lineFine}`, py: 1.4, px: 2 };

/* ── Status dot ──────────────────────────────────────────────────── */
const StatusDot = ({ color, label }) => (
  <Box display="flex" alignItems="center" gap={0.8}>
    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
    <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color }}>{label}</Typography>
  </Box>
);

/* ── Main ─────────────────────────────────────────────────────────── */
const AdminDashboard = () => {
  const navigate = useNavigate();
  const tabsRef  = useRef(null);
  const [tab, setTab] = useState(0);

  const [loading, setLoading] = useState(true);
  const [stats, setStats]   = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [recentUsers,  setRecentUsers]  = useState([]);

  const [users,       setUsers]       = useState([]);
  const [usersTotal,  setUsersTotal]  = useState(1);
  const [usersPage,   setUsersPage]   = useState(1);
  const [usersSearch, setUsersSearch] = useState('');
  const [usersType,   setUsersType]   = useState('');
  const [usersStatus, setUsersStatus] = useState('');
  const [usersLoad,   setUsersLoad]   = useState(false);

  const [orders,       setOrders]       = useState([]);
  const [ordersTotal,  setOrdersTotal]  = useState(1);
  const [ordersPage,   setOrdersPage]   = useState(1);
  const [ordersSearch, setOrdersSearch] = useState('');
  const [ordersStatus, setOrdersStatus] = useState('');
  const [ordersLoad,   setOrdersLoad]   = useState(false);

  const [restaurants,      setRestaurants]      = useState([]);
  const [restaurantsTotal, setRestaurantsTotal] = useState(1);
  const [restaurantsPage,  setRestaurantsPage]  = useState(1);
  const [restaurantsSearch,setRestaurantsSearch]= useState('');
  const [restaurantsLoad,  setRestaurantsLoad]  = useState(false);

  const [commissionStats,   setCommissionStats]   = useState(null);
  const [commissionPartners,setCommissionPartners]= useState([]);
  const [commissionPeriod,  setCommissionPeriod]  = useState('month');
  const [commissionRates,   setCommissionRates]   = useState([]);
  const [commissionLoad,    setCommissionLoad]    = useState(false);

  const [payouts,      setPayouts]      = useState([]);
  const [payoutTotals, setPayoutTotals] = useState(null);
  const [payoutFilter, setPayoutFilter] = useState('');
  const [payoutsLoad,  setPayoutsLoad]  = useState(false);

  const [pendingVehicles, setPendingVehicles] = useState([]);
  const [vehiclesLoad,    setVehiclesLoad]    = useState(false);
  const [vehiclePreview,  setVehiclePreview]  = useState(null);
  const [rejectDialog,    setRejectDialog]    = useState(null);
  const [rejectReason,    setRejectReason]    = useState('');

  const [editUser,    setEditUser]    = useState(null);
  const [editOrder,   setEditOrder]   = useState(null);
  const [editStatus,  setEditStatus]  = useState('');
  const [error,       setError]       = useState('');
  const [success,     setSuccess]     = useState('');
  const [pendingPartners, setPendingPartners] = useState(0);

  const [pushTitle,    setPushTitle]    = useState('');
  const [pushBody,     setPushBody]     = useState('');
  const [pushTarget,   setPushTarget]   = useState('all');
  const [pushImageUrl, setPushImageUrl] = useState('');
  const [pushDeepLink, setPushDeepLink] = useState('');
  const [pushLoading,  setPushLoading]  = useState(false);
  const [pushResult,   setPushResult]   = useState(null);
  const [campaigns,    setCampaigns]    = useState([]);
  const [campaignsLoad,setCampaignsLoad]= useState(false);

  /* ── Loaders ─────────────────────────────────────────────────── */
  const loadDashboard = useCallback(async () => {
    try {
      const data = await adminService.getDashboardStats();
      setStats(data.stats); setRecentOrders(data.recentOrders || []); setRecentUsers(data.recentUsers || []);
    } catch { setError('Erreur chargement dashboard'); } finally { setLoading(false); }
  }, []);

  const loadPendingPartners = useCallback(async () => {
    try {
      const data = await partnerService.getStats();
      setPendingPartners(parseInt(data.stats?.pending) || 0);
    } catch { /* silently ignore */ }
  }, []);

  const loadUsers = useCallback(async () => {
    setUsersLoad(true);
    try {
      const data = await adminService.getUsers({ page: usersPage, limit: 15, search: usersSearch, user_type: usersType, status: usersStatus });
      setUsers(data.users || []); setUsersTotal(data.totalPages || 1);
    } catch { setError('Erreur utilisateurs'); } finally { setUsersLoad(false); }
  }, [usersPage, usersSearch, usersType, usersStatus]);

  const loadOrders = useCallback(async () => {
    setOrdersLoad(true);
    try {
      const data = await adminService.getOrders({ page: ordersPage, limit: 15, search: ordersSearch, status: ordersStatus });
      setOrders(data.orders || []); setOrdersTotal(data.totalPages || 1);
    } catch { setError('Erreur commandes'); } finally { setOrdersLoad(false); }
  }, [ordersPage, ordersSearch, ordersStatus]);

  const loadRestaurants = useCallback(async () => {
    setRestaurantsLoad(true);
    try {
      const data = await adminService.getRestaurants({ page: restaurantsPage, limit: 15, search: restaurantsSearch });
      setRestaurants(data.restaurants || []); setRestaurantsTotal(data.totalPages || 1);
    } catch { setError('Erreur restaurants'); } finally { setRestaurantsLoad(false); }
  }, [restaurantsPage, restaurantsSearch]);

  const loadPayouts = useCallback(async () => {
    setPayoutsLoad(true);
    try {
      const res = await api.get(`/admin/payouts?status=${payoutFilter}&limit=50`);
      setPayouts(res.data.payouts || []);
      setPayoutTotals(res.data.totals || null);
    } catch { setError('Erreur chargement virements'); } finally { setPayoutsLoad(false); }
  }, [payoutFilter]);

  const loadVehicles = useCallback(async () => {
    setVehiclesLoad(true);
    try {
      const res = await api.get('/car-rentals/admin/pending');
      setPendingVehicles(res.data.pending || []);
    } catch { setError('Erreur chargement véhicules'); } finally { setVehiclesLoad(false); }
  }, []);

  const handleApproveVehicle = async (carId) => {
    try {
      await api.put(`/car-rentals/admin/verify/${carId}`, { status: 'verified' });
      showSuccess('Véhicule approuvé'); loadVehicles();
    } catch { setError('Erreur approbation'); }
  };

  const handleRejectVehicle = async () => {
    if (!rejectDialog) return;
    try {
      await api.put(`/car-rentals/admin/verify/${rejectDialog}`, { status: 'rejected', rejectionReason: rejectReason });
      setRejectDialog(null); setRejectReason('');
      showSuccess('Véhicule refusé'); loadVehicles();
    } catch { setError('Erreur refus'); }
  };

  const loadCommissions = useCallback(async () => {
    setCommissionLoad(true);
    try {
      const [statsRes, partnersRes, ratesRes] = await Promise.all([
        api.get(`/admin/commissions/stats?period=${commissionPeriod}`),
        api.get(`/admin/commissions/partners?period=${commissionPeriod}`),
        api.get('/admin/commissions/rates').catch(() => ({ data: { rates: [] } })),
      ]);
      setCommissionStats(statsRes.data.stats);
      setCommissionPartners(partnersRes.data.partners || []);
      setCommissionRates(ratesRes.data.rates || []);
    } catch { setError('Erreur commissions'); } finally { setCommissionLoad(false); }
  }, [commissionPeriod]);

  const loadCampaigns = useCallback(async () => {
    setCampaignsLoad(true);
    try {
      const res = await api.get('/admin/marketing-campaigns');
      setCampaigns(res.data.campaigns || []);
    } catch { /* ignore */ } finally { setCampaignsLoad(false); }
  }, []);

  const handleSendPush = async () => {
    if (!pushTitle.trim() || !pushBody.trim()) return;
    setPushLoading(true); setPushResult(null);
    try {
      const res = await api.post('/marketing-push', {
        title: pushTitle.trim(),
        body: pushBody.trim(),
        target: pushTarget,
        ...(pushImageUrl.trim() ? { image_url: pushImageUrl.trim() } : {}),
        ...(pushDeepLink.trim() ? { deep_link: pushDeepLink.trim() } : {}),
      });
      setPushResult({ success: true, ...res.data });
      setPushTitle(''); setPushBody(''); setPushImageUrl(''); setPushDeepLink('');
      loadCampaigns();
    } catch (e) {
      setPushResult({ success: false, message: e.response?.data?.error || 'Erreur envoi' });
    } finally { setPushLoading(false); }
  };

  useEffect(() => { loadDashboard(); loadPendingPartners(); }, [loadDashboard, loadPendingPartners]);
  useEffect(() => { if (tab === 1) loadUsers(); }, [tab, loadUsers]);
  useEffect(() => { if (tab === 2) loadOrders(); }, [tab, loadOrders]);
  useEffect(() => { if (tab === 3) loadRestaurants(); }, [tab, loadRestaurants]);
  useEffect(() => { if (tab === 4) loadCommissions(); }, [tab, loadCommissions]);
  useEffect(() => { if (tab === 5) loadPayouts(); }, [tab, loadPayouts]);
  useEffect(() => { if (tab === 6) loadVehicles(); }, [tab, loadVehicles]);
  useEffect(() => { if (tab === 7) loadCampaigns(); }, [tab, loadCampaigns]);

  const showSuccess = (msg) => { setSuccess(msg); setTimeout(() => setSuccess(''), 3000); };

  const goTab = (index) => {
    setTab(index);
    setTimeout(() => tabsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };

  const handleToggleUser = async (user) => {
    try {
      await adminService.updateUser(user.id, { is_active: !user.is_active });
      showSuccess(`Utilisateur ${user.is_active ? 'désactivé' : 'activé'}`); loadUsers();
    } catch { setError('Erreur mise à jour'); }
  };

  const handleSaveUser = async () => {
    try {
      await adminService.updateUser(editUser.id, { is_active: editUser.is_active, is_verified: editUser.is_verified, user_type: editUser.user_type });
      setEditUser(null); showSuccess('Utilisateur mis à jour'); loadUsers();
    } catch { setError('Erreur modification'); }
  };

  const handleUpdateOrder = async () => {
    try {
      await adminService.updateOrderStatus(editOrder.id, editStatus);
      setEditOrder(null); showSuccess('Statut mis à jour'); loadOrders();
    } catch { setError('Erreur statut commande'); }
  };

  const handleToggleRestaurant = async (r) => {
    try {
      await adminService.updateRestaurant(r.id, { is_open: !r.is_open });
      showSuccess(`Restaurant ${r.is_open ? 'fermé' : 'ouvert'}`); loadRestaurants();
    } catch { setError('Erreur restaurant'); }
  };

  /* ── Loading state ──────────────────────────────────────────── */
  if (loading) return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" sx={{ bgcolor: P.bg }}>
      <Box textAlign="center">
        <CircularProgress sx={{ color: P.gold, mb: 2 }} size={32} thickness={2} />
        <Typography sx={{ color: P.muted, fontSize: '0.75rem', letterSpacing: '0.08em' }}>Chargement...</Typography>
      </Box>
    </Box>
  );

  const u = stats?.users || {};
  const o = stats?.orders || {};
  const d = stats?.drivers || {};
  const partnerData = stats?.partners || [];

  const TABS = ['Vue d\'ensemble', 'Utilisateurs', 'Commandes', 'Restaurants', 'Commissions', 'Virements', 'Véhicules', 'Notifications', 'Suppressions', 'Messagerie', 'Stripe'];

  /* shared input style */
  const inputSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '4px', bgcolor: 'rgba(29,21,48,0.03)',
      '& fieldset': { borderColor: P.line, borderWidth: '0.5px' },
      '&:hover fieldset': { borderColor: 'rgba(29,21,48,0.15)' },
      '&.Mui-focused fieldset': { borderColor: P.gold, borderWidth: '0.5px' },
    },
    '& .MuiInputBase-input': { color: P.text, fontSize: '0.82rem' },
    '& .MuiInputLabel-root': { color: P.muted, fontSize: '0.78rem' },
  };

  const group = NAV.find((g) => g.tabs.includes(tab)) || NAV[0];
  const navBadge = (label) => (label === 'Partenaires' && pendingPartners > 0 ? pendingPartners : 0);

  return (
    <ThemeProvider theme={adminTheme}>
    <Box sx={{ minHeight: '100vh', bgcolor: P.bg, fontFamily: FONT, color: P.text }}>

      {/* ── Barre du haut (maquette B) ─────────────────────────── */}
      <Box sx={{ bgcolor: P.surface, borderBottom: `1px solid ${P.line}`, position: 'sticky', top: 0, zIndex: 10 }}>
        <Container maxWidth="lg">
          <Box display="flex" alignItems="center" gap={3} sx={{ minHeight: 60 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '1.15rem', color: P.purple, letterSpacing: '-0.02em', flexShrink: 0 }}>
              Usafiri <Box component="span" sx={{ color: P.muted, fontWeight: 500 }}>admin</Box>
            </Typography>
            <Box ref={tabsRef} component="nav" aria-label="Sections de l'admin"
              sx={{ display: 'flex', gap: 0.5, overflowX: 'auto', flex: 1, alignSelf: 'stretch', scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
              {NAV.map((g) => {
                const active = g === group;
                const n = navBadge(g.label);
                return (
                  <Box key={g.label} component="button" type="button" onClick={() => setTab(g.tabs[0])}
                    aria-current={active ? 'page' : undefined}
                    sx={{
                      all: 'unset', cursor: 'pointer', px: 1.5, display: 'flex', alignItems: 'center', gap: 0.75,
                      fontSize: '0.88rem', fontWeight: active ? 700 : 500, whiteSpace: 'nowrap',
                      color: active ? P.text : P.soft,
                      borderBottom: `2px solid ${active ? P.purple : 'transparent'}`,
                      '&:hover': { color: P.text },
                      '&:focus-visible': { outline: `2px solid ${P.purple}`, outlineOffset: -2 },
                    }}>
                    {g.label}
                    {n > 0 && <Box component="span" sx={{ bgcolor: P.red, color: '#fff', borderRadius: '999px', fontSize: '0.68rem', fontWeight: 700, px: 0.8, lineHeight: '18px' }}>{n}</Box>}
                  </Box>
                );
              })}
            </Box>
            <Box display="flex" gap={1} alignItems="center" sx={{ flexShrink: 0 }}>
              <Tooltip title="Vérification partenaires">
                <IconButton onClick={() => navigate('/admin/partner-verification')} aria-label="Vérification partenaires"
                  sx={{ bgcolor: '#EDE8F2', border: `2px solid ${P.text}`, color: P.text, width: 38, height: 38, '&:hover': { bgcolor: '#e2daea' } }}>
                  <Badge badgeContent={pendingPartners} sx={{ '& .MuiBadge-badge': { bgcolor: P.red, color: '#fff', fontSize: '0.6rem', minWidth: 16, height: 16 } }}>
                    <VerifiedUserIcon sx={{ fontSize: 18 }} />
                  </Badge>
                </IconButton>
              </Tooltip>
              <Tooltip title="Rafraîchir">
                <IconButton onClick={loadDashboard} aria-label="Rafraîchir"
                  sx={{ bgcolor: '#EDE8F2', border: `2px solid ${P.text}`, color: P.text, width: 38, height: 38, '&:hover': { bgcolor: '#e2daea' } }}>
                  <RefreshIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ pt: 3, pb: 8 }}>
        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: '10px' }} onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2, borderRadius: '10px' }} onClose={() => setSuccess('')}>{success}</Alert>}

        <Box display="flex" alignItems="baseline" justifyContent="space-between" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
          <Typography component="h1" sx={{ fontSize: '1.6rem', fontWeight: 700, letterSpacing: '-0.02em' }}>{group.label}</Typography>
          <Typography sx={{ fontSize: '0.82rem', color: P.soft }}>
            {new Date().toLocaleDateString('fr-FR', { timeZone: TZ, weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </Typography>
        </Box>

        {/* Sous-onglets quand une section en regroupe plusieurs */}
        {group.tabs.length > 1 && (
          <Box display="flex" gap={1} sx={{ mb: 3 }}>
            {group.tabs.map((t) => {
              const on = t === tab;
              return (
                <Box key={t} component="button" type="button" onClick={() => setTab(t)}
                  sx={{
                    all: 'unset', cursor: 'pointer', px: 2, py: 0.8, borderRadius: '999px', fontSize: '0.82rem', fontWeight: 700,
                    bgcolor: on ? P.purple : '#EDE8F2', color: on ? '#fff' : P.text, border: `2px solid ${on ? P.purple : P.text}`,
                    '&:focus-visible': { outline: `2px solid ${P.purple}`, outlineOffset: 2 },
                  }}>
                  {TABS[t]}
                </Box>
              );
            })}
          </Box>
        )}

        {/* ── Accueil : courbe + chiffres clés ───────────────────── */}
        {tab === 0 && (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0,2fr) minmax(0,1fr)' }, gap: 2, mb: 1 }}>
            <RevenueChart daily={o.daily} />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: '1fr' }, gap: 1.5 }}>
              <Stat label="Commandes aujourd'hui" value={fmt(parseInt(o.orders_today || 0, 10))} sub={`${fmt(parseInt(o.active_orders || 0, 10))} en cours`} />
              <Stat label="Ventes aujourd'hui" value={fmtEur(parseFloat(o.revenue_today || 0).toFixed(2))} sub={`${fmtEur(parseFloat(o.total_revenue || 0).toFixed(2))} au total`} />
              <Stat label="CA Usafiri · ce mois" value={fmtEur(o.ca_month)}
                sub={`net ≈ ${fmtEur((parseFloat(o.ca_month) || 0) - (parseFloat(o.stripe_fees_month) || 0))} après frais Stripe · ${fmtEur(o.ca_today)} aujourd'hui`}
                accent={P.purple} />
              <Stat label="Utilisateurs" value={fmt(parseInt(u.total_users || 0, 10))} sub={`+${u.new_users_30d || 0} ce mois`} />
            </Box>
          </Box>
        )}

        {/* ── Accueil : détail du CA Usafiri ─────────────────────── */}
        {tab === 0 && (
          <Box sx={{ bgcolor: P.surface, border: `1px solid ${P.line}`, borderRadius: '12px', p: 2.5, mt: 2 }}>
            <Typography sx={{ fontWeight: 700, mb: 0.5 }}>Chiffre d'affaires Usafiri</Typography>
            <Typography sx={{ fontSize: '0.8rem', color: P.soft, mb: 2 }}>
              Ce que la plateforme garde sur les commandes payées et terminées : commission restaurant, commission livreur et frais de service.
              Les frais Stripe sont une estimation (1,5 % + 0,25 € par paiement) ; le montant exact est dans Argent → Stripe.
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, minmax(0,1fr))' }, gap: 1.5 }}>
              {[
                { l: 'Ce mois', v: o.ca_month, s: `${fmt(parseInt(o.orders_month || 0, 10))} commande${parseInt(o.orders_month || 0, 10) > 1 ? 's' : ''}` },
                { l: 'Frais Stripe · ce mois', v: -(parseFloat(o.stripe_fees_month) || 0), s: 'estimation' },
                { l: 'Net · ce mois', v: (parseFloat(o.ca_month) || 0) - (parseFloat(o.stripe_fees_month) || 0), s: 'après frais Stripe', strong: true },
                { l: 'Depuis le début', v: o.ca_total, s: `net ≈ ${fmtEur((parseFloat(o.ca_total) || 0) - (parseFloat(o.stripe_fees_total) || 0))}` },
              ].map((k) => (
                <Box key={k.l} sx={{ bgcolor: P.bg, borderRadius: '10px', p: 1.5 }}>
                  <Typography sx={{ fontSize: '0.75rem', color: P.soft }}>{k.l}</Typography>
                  <Typography sx={{ ...monoNum, fontSize: '1.25rem', color: k.strong ? P.purple : P.text }}>{fmtEur(k.v)}</Typography>
                  <Typography sx={{ fontSize: '0.72rem', color: P.soft }}>{k.s}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* ════════════════════════════════════════════════════════
            TAB 0 -- VUE D'ENSEMBLE
        ════════════════════════════════════════════════════════ */}
        {tab === 0 && (
          <Box sx={{ mt: 4 }}>
            <Grid container spacing={0}>

              {/* Left column -- Users + Orders breakdown */}
              <Grid item xs={12} md={8} sx={{ pr: { md: 4 }, borderRight: { md: `0.5px solid ${P.line}` } }}>

                {/* Repartition utilisateurs */}
                <Typography sx={{ ...micro, mb: 2 }}>Répartition utilisateurs</Typography>
                <Box sx={{ mb: 4 }}>
                  {[
                    { label: 'Clients',       count: u.total_clients,           color: P.blue },
                    { label: 'Chauffeurs VTC', count: u.total_drivers,           color: P.purple },
                    { label: 'Livreurs',       count: u.total_delivery,          color: P.orange },
                    { label: 'Restaurateurs',  count: u.total_restaurant_owners, color: P.red },
                  ].map((item, i) => (
                    <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      py: 1.2, borderBottom: `0.5px solid ${P.lineFine}` }}>
                      <Box display="flex" alignItems="center" gap={1.5}>
                        <Box sx={{ width: 3, height: 16, bgcolor: item.color, borderRadius: '1px' }} />
                        <Typography sx={{ fontSize: '0.82rem', color: P.soft }}>{item.label}</Typography>
                      </Box>
                      <Typography sx={{ ...monoNum, fontSize: '0.9rem', color: P.text }}>{item.count || 0}</Typography>
                    </Box>
                  ))}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1.5 }}>
                    <Typography sx={{ fontSize: '0.7rem', color: P.muted }}>Comptes actifs</Typography>
                    <Typography sx={{ ...monoNum, fontSize: '0.78rem', color: P.green }}>{u.active_users || 0}</Typography>
                  </Box>
                </Box>

                <Sep />

                {/* Partenaires par service */}
                <Typography sx={{ ...micro, mb: 2, mt: 3 }}>Partenaires par service</Typography>
                <Box sx={{ mb: 4 }}>
                  {(() => {
                    const partnerTypeConfig = [
                      { key: 'car_rental',  label: 'Location',    icon: <CarRentalIcon sx={{ fontSize: 14 }} />,     color: P.blue },
                      { key: 'delivery',    label: 'Livreur',     icon: <TwoWheelerIcon sx={{ fontSize: 14 }} />,    color: P.orange },
                      { key: 'store',       label: 'Courses',     icon: <ShoppingBagIcon sx={{ fontSize: 14 }} />,   color: '#06b6d4' },
                      { key: 'vtc_driver',  label: 'VTC',         icon: <DirectionsCarIcon sx={{ fontSize: 14 }} />, color: P.purple },
                      { key: 'restaurant',  label: 'Restaurant',  icon: <RestaurantIcon sx={{ fontSize: 14 }} />,    color: P.red },
                      { key: 'logistics',   label: 'Logistique',  icon: <LocalShippingIcon sx={{ fontSize: 14 }} />, color: P.green },
                      { key: 'carpool',     label: 'Covoiturage', icon: <GroupsIcon sx={{ fontSize: 14 }} />,        color: P.gold },
                    ];
                    return partnerTypeConfig.map((pt, i) => {
                      const data = partnerData.find(p => p.partner_type === pt.key);
                      const approved = parseInt(data?.approved || 0);
                      const pending = parseInt(data?.pending || 0);
                      const rejected = parseInt(data?.rejected || 0);
                      const total = approved + pending + rejected;
                      return (
                        <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                          py: 1.2, borderBottom: `0.5px solid ${P.lineFine}` }}>
                          <Box display="flex" alignItems="center" gap={1.5}>
                            <Box sx={{ color: pt.color, display: 'flex', width: 20, justifyContent: 'center' }}>{pt.icon}</Box>
                            <Typography sx={{ fontSize: '0.82rem', color: P.soft }}>{pt.label}</Typography>
                          </Box>
                          <Box display="flex" alignItems="center" gap={2}>
                            {pending > 0 && (
                              <Typography sx={{ fontSize: '0.62rem', fontWeight: 600, color: P.orange }}>
                                {pending} en attente
                              </Typography>
                            )}
                            <Box display="flex" alignItems="center" gap={0.8}>
                              <Typography sx={{ ...monoNum, fontSize: '0.82rem', color: approved > 0 ? P.green : P.muted }}>{approved}</Typography>
                              <Typography sx={{ fontSize: '0.58rem', color: P.muted }}>/ {total}</Typography>
                            </Box>
                          </Box>
                        </Box>
                      );
                    });
                  })()}
                </Box>

                <Sep />

                {/* Statut commandes */}
                <Typography sx={{ ...micro, mb: 2, mt: 3 }}>Statut des commandes</Typography>
                <Box sx={{ display: 'flex', gap: 4, mb: 3 }}>
                  {[
                    { label: 'Livrées',  count: o.delivered_orders, color: P.green },
                    { label: 'En cours', count: o.active_orders,    color: P.blue },
                    { label: 'Annulées', count: o.cancelled_orders, color: P.red },
                  ].map((item, i) => (
                    <Box key={i}>
                      <Typography sx={{ ...monoNum, fontSize: '1.4rem', color: item.color, lineHeight: 1 }}>{item.count || 0}</Typography>
                      <Typography sx={{ fontSize: '0.68rem', color: P.muted, mt: 0.5 }}>{item.label}</Typography>
                    </Box>
                  ))}
                  <Box sx={{ ml: 'auto' }}>
                    <Typography sx={{ ...monoNum, fontSize: '1.4rem', color: P.orange, lineHeight: 1 }}>{d.available_drivers || 0}</Typography>
                    <Typography sx={{ fontSize: '0.68rem', color: P.muted, mt: 0.5 }}>Livreurs dispo</Typography>
                  </Box>
                </Box>

                <Sep />

                {/* Dernieres commandes */}
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography sx={{ ...micro }}>Dernières commandes</Typography>
                  <Typography onClick={() => setTab(2)} sx={{ fontSize: '0.68rem', color: P.soft, cursor: 'pointer',
                    '&:hover': { color: P.gold } }}>
                    Tout voir
                  </Typography>
                </Box>
                {recentOrders.length === 0
                  ? <Typography sx={{ fontSize: '0.78rem', color: P.muted, py: 3, textAlign: 'center' }}>Aucune commande</Typography>
                  : recentOrders.slice(0, 7).map((order) => {
                      const s = orderStatusMap[order.order_status] || {};
                      return (
                        <Box key={order.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                          py: 1.2, borderBottom: `0.5px solid ${P.lineFine}` }}>
                          <Box>
                            <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: P.text }}>{order.order_number}</Typography>
                            <Typography sx={{ fontSize: '0.68rem', color: P.muted }}>{order.restaurant_name} -- {order.customer_name}</Typography>
                          </Box>
                          <Box textAlign="right" display="flex" alignItems="center" gap={2}>
                            <StatusDot color={s.color} label={s.label} />
                            <Typography sx={{ ...monoNum, fontSize: '0.82rem', color: P.gold }}>{fmtEur(order.total_amount)}</Typography>
                          </Box>
                        </Box>
                      );
                    })}
              </Grid>

              {/* Right column -- Revenue + Quick access */}
              <Grid item xs={12} md={4} sx={{ pl: { md: 4 }, mt: { xs: 4, md: 0 } }}>

                {/* Revenus */}
                <Typography sx={{ ...micro, mb: 2 }}>Revenus</Typography>
                {[
                  { label: 'CA Total',       value: fmtEur(o.total_revenue), color: P.gold },
                  { label: 'CA aujourd\'hui', value: fmtEur(o.revenue_today), color: P.text },
                ].map((item, i) => (
                  <Box key={i} sx={{ py: 1.5, borderBottom: `0.5px solid ${P.lineFine}` }}>
                    <Typography sx={{ fontSize: '0.68rem', color: P.muted, mb: 0.3 }}>{item.label}</Typography>
                    <Typography sx={{ ...monoNum, fontSize: '1.3rem', color: item.color, lineHeight: 1 }}>{item.value}</Typography>
                  </Box>
                ))}

                <Typography sx={{ ...micro, mt: 3, mb: 1.5, color: P.gold }}>Commission Usafiri</Typography>
                {[
                  { label: 'Aujourd\'hui',  value: fmtEur(parseFloat(o.commission_today  || 0).toFixed(2)) },
                  { label: 'Cette semaine', value: fmtEur(parseFloat(o.commission_week   || 0).toFixed(2)) },
                  { label: 'Ce mois',       value: fmtEur(parseFloat(o.commission_month  || 0).toFixed(2)) },
                  { label: 'Total cumule',  value: fmtEur(parseFloat(o.commission_total  || 0).toFixed(2)) },
                ].map((item, i) => (
                  <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.8, borderBottom: `0.5px solid ${P.lineFine}` }}>
                    <Typography sx={{ fontSize: '0.72rem', color: P.muted }}>{item.label}</Typography>
                    <Typography sx={{ ...monoNum, fontSize: '0.78rem', color: P.green }}>{item.value}</Typography>
                  </Box>
                ))}

                <Box sx={{ mt: 2, py: 1.5, px: 2, borderLeft: `2px solid ${P.green}`, bgcolor: 'rgba(34,197,94,0.04)' }}>
                  <Typography sx={{ fontSize: '0.68rem', color: P.soft }}>
                    15% sous-total restaurant / 5% frais livraison
                  </Typography>
                </Box>

                <Sep />

                {/* Acces rapides */}
                <Typography sx={{ ...micro, mt: 3, mb: 2 }}>Acces rapides</Typography>
                {[
                  { label: 'Verification partenaires', onClick: () => navigate('/admin/partner-verification') },
                  { label: 'Commissions Stripe',       onClick: () => goTab(4) },
                  { label: 'Gerer utilisateurs',       onClick: () => goTab(1) },
                  { label: 'Toutes les commandes',     onClick: () => goTab(2) },
                ].map((item, i) => (
                  <Box key={i} onClick={item.onClick}
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      py: 1.3, borderBottom: `0.5px solid ${P.lineFine}`, cursor: 'pointer',
                      '&:hover .arrow': { opacity: 1, transform: 'translateX(0)' },
                      '&:hover': { bgcolor: 'rgba(29,21,48,0.02)' } }}>
                    <Typography sx={{ fontSize: '0.82rem', color: P.soft }}>{item.label}</Typography>
                    <ArrowForwardIcon className="arrow" sx={{ fontSize: 14, color: P.gold, opacity: 0, transform: 'translateX(-4px)', transition: 'all 0.2s' }} />
                  </Box>
                ))}

                {/* Nouveaux utilisateurs */}
                <Box sx={{ mt: 4 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography sx={{ ...micro }}>Nouveaux inscrits</Typography>
                    <Typography onClick={() => setTab(1)} sx={{ fontSize: '0.68rem', color: P.soft, cursor: 'pointer', '&:hover': { color: P.gold } }}>Voir</Typography>
                  </Box>
                  {recentUsers.length === 0
                    ? <Typography sx={{ fontSize: '0.78rem', color: P.muted, py: 2 }}>Aucun</Typography>
                    : recentUsers.slice(0, 5).map((user) => {
                        const ut = userTypeMap[user.user_type] || userTypeMap.client;
                        return (
                          <Box key={user.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            py: 1, borderBottom: `0.5px solid ${P.lineFine}` }}>
                            <Box display="flex" alignItems="center" gap={1.2}>
                              <Box sx={{ width: 28, height: 28, borderRadius: '4px', bgcolor: `${ut.color}12`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: ut.color, fontSize: '0.7rem', fontWeight: 700 }}>
                                {user.first_name?.[0]}
                              </Box>
                              <Box>
                                <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: P.text }}>{user.first_name} {user.last_name}</Typography>
                                <Typography sx={{ fontSize: '0.62rem', color: P.muted }}>{user.email}</Typography>
                              </Box>
                            </Box>
                            <Typography sx={{ fontSize: '0.62rem', fontWeight: 600, color: ut.color }}>{ut.label}</Typography>
                          </Box>
                        );
                      })}
                </Box>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* ════════════════════════════════════════════════════════
            TAB 1 -- UTILISATEURS
        ════════════════════════════════════════════════════════ */}
        {tab === 1 && (
          <Box sx={{ mt: 3 }}>
            {/* Filters */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr 1fr auto' }, gap: 1.5, mb: 3, p: 2, border: `0.5px solid ${P.line}`, borderRadius: '4px' }}>
              <TextField fullWidth size="small" placeholder="Nom, email, telephone..."
                value={usersSearch} onChange={(e) => { setUsersSearch(e.target.value); setUsersPage(1); }}
                InputProps={{ startAdornment: <SearchIcon sx={{ color: P.muted, fontSize: 16, mr: 1 }} /> }}
                sx={inputSx} />
              <TextField select fullWidth size="small" label="Type" value={usersType}
                onChange={(e) => { setUsersType(e.target.value); setUsersPage(1); }} sx={inputSx}>
                <MenuItem value="">Tous</MenuItem>
                <MenuItem value="client">Clients</MenuItem>
                <MenuItem value="driver">Chauffeurs</MenuItem>
                <MenuItem value="delivery">Livreurs</MenuItem>
                <MenuItem value="restaurant_owner">Restaurateurs</MenuItem>
                <MenuItem value="admin">Admins</MenuItem>
              </TextField>
              <TextField select fullWidth size="small" label="Statut" value={usersStatus}
                onChange={(e) => { setUsersStatus(e.target.value); setUsersPage(1); }} sx={inputSx}>
                <MenuItem value="">Tous</MenuItem>
                <MenuItem value="active">Actifs</MenuItem>
                <MenuItem value="inactive">Inactifs</MenuItem>
              </TextField>
              <IconButton onClick={loadUsers} sx={{ color: P.soft, border: `0.5px solid ${P.line}`, borderRadius: '4px', width: 40, height: 40, '&:hover': { borderColor: P.gold, color: P.gold } }}>
                <RefreshIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>

            {/* Table */}
            {usersLoad ? <Box display="flex" justifyContent="center" py={6}><CircularProgress sx={{ color: P.gold }} size={28} thickness={2} /></Box> : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={thSx}>Utilisateur</TableCell>
                      <TableCell sx={thSx}>Type</TableCell>
                      <TableCell sx={thSx}>Contact</TableCell>
                      <TableCell sx={thSx} align="center">Statut</TableCell>
                      <TableCell sx={thSx} align="center">Verifie</TableCell>
                      <TableCell sx={thSx} align="center">Note</TableCell>
                      <TableCell sx={thSx}>Inscription</TableCell>
                      <TableCell sx={thSx} align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {users.map((user) => {
                      const ut = userTypeMap[user.user_type] || userTypeMap.client;
                      return (
                        <TableRow key={user.id} sx={{ '&:hover': { bgcolor: 'rgba(29,21,48,0.015)' } }}>
                          <TableCell sx={tdSx}>
                            <Box display="flex" alignItems="center" gap={1.5}>
                              <Box sx={{ width: 32, height: 32, borderRadius: '4px', bgcolor: `${ut.color}10`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: ut.color, fontSize: '0.75rem', fontWeight: 700 }}>
                                {user.first_name?.[0]}
                              </Box>
                              <Box>
                                <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: P.text }}>{user.first_name} {user.last_name}</Typography>
                                <Typography sx={{ fontSize: '0.62rem', color: P.muted }}>#{user.id}</Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell sx={tdSx}>
                            <StatusDot color={ut.color} label={ut.label} />
                          </TableCell>
                          <TableCell sx={tdSx}>
                            <Typography sx={{ fontSize: '0.78rem', color: P.text }}>{user.email}</Typography>
                            <Typography sx={{ fontSize: '0.65rem', color: P.muted }}>{user.phone}</Typography>
                          </TableCell>
                          <TableCell sx={tdSx} align="center">
                            <StatusDot color={user.is_active ? P.green : P.red} label={user.is_active ? 'Actif' : 'Inactif'} />
                          </TableCell>
                          <TableCell sx={tdSx} align="center">
                            {user.is_verified
                              ? <CheckCircleIcon sx={{ color: P.green, fontSize: 15 }} />
                              : <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: P.muted, mx: 'auto' }} />}
                          </TableCell>
                          <TableCell sx={tdSx} align="center">
                            <Typography sx={{ ...monoNum, fontSize: '0.78rem', color: user.rating > 0 ? P.gold : P.muted }}>
                              {user.rating > 0 ? user.rating : '--'}
                            </Typography>
                          </TableCell>
                          <TableCell sx={tdSx}>
                            <Typography sx={{ fontSize: '0.72rem', color: P.muted }}>
                              {fmtDateTZ(user.created_at, { day: 'numeric', month: 'short', year: '2-digit' })}
                            </Typography>
                          </TableCell>
                          <TableCell sx={tdSx} align="center">
                            <Box display="flex" justifyContent="center" gap={0.5}>
                              <IconButton size="small" onClick={() => setEditUser({ ...user })}
                                sx={{ color: P.soft, '&:hover': { color: P.blue } }}>
                                <EditIcon sx={{ fontSize: 15 }} />
                              </IconButton>
                              <IconButton size="small" onClick={() => handleToggleUser(user)}
                                sx={{ color: P.soft, '&:hover': { color: user.is_active ? P.red : P.green } }}>
                                {user.is_active ? <BlockIcon sx={{ fontSize: 15 }} /> : <CheckCircleIcon sx={{ fontSize: 15 }} />}
                              </IconButton>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {users.length === 0 && (
                      <TableRow><TableCell colSpan={8} sx={{ ...tdSx, textAlign: 'center', py: 5, color: P.muted }}>Aucun utilisateur trouve</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            {usersTotal > 1 && (
              <Box display="flex" justifyContent="center" mt={3}>
                <Pagination count={usersTotal} page={usersPage} onChange={(_, v) => setUsersPage(v)} size="small"
                  sx={{ '& .MuiPaginationItem-root': { color: P.soft, borderColor: P.line, fontSize: '0.72rem' }, '& .Mui-selected': { bgcolor: `${P.purple} !important`, color: 'white !important' } }} />
              </Box>
            )}
          </Box>
        )}

        {/* ════════════════════════════════════════════════════════
            TAB 2 -- COMMANDES
        ════════════════════════════════════════════════════════ */}
        {tab === 2 && (
          <Box sx={{ mt: 3 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr auto' }, gap: 1.5, mb: 3, p: 2, border: `0.5px solid ${P.line}`, borderRadius: '4px' }}>
              <TextField fullWidth size="small" placeholder="N. commande, client..."
                value={ordersSearch} onChange={(e) => { setOrdersSearch(e.target.value); setOrdersPage(1); }}
                InputProps={{ startAdornment: <SearchIcon sx={{ color: P.muted, fontSize: 16, mr: 1 }} /> }}
                sx={inputSx} />
              <TextField select fullWidth size="small" label="Statut" value={ordersStatus}
                onChange={(e) => { setOrdersStatus(e.target.value); setOrdersPage(1); }} sx={inputSx}>
                <MenuItem value="">Tous</MenuItem>
                {Object.entries(orderStatusMap).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
              </TextField>
              <IconButton onClick={loadOrders} sx={{ color: P.soft, border: `0.5px solid ${P.line}`, borderRadius: '4px', width: 40, height: 40, '&:hover': { borderColor: P.gold, color: P.gold } }}>
                <RefreshIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>

            {ordersLoad ? <Box display="flex" justifyContent="center" py={6}><CircularProgress sx={{ color: P.gold }} size={28} thickness={2} /></Box> : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={thSx}>Commande</TableCell>
                      <TableCell sx={thSx}>Restaurant</TableCell>
                      <TableCell sx={thSx}>Client</TableCell>
                      <TableCell sx={thSx} align="right">Montant</TableCell>
                      <TableCell sx={thSx} align="center">Statut</TableCell>
                      <TableCell sx={thSx}>Date</TableCell>
                      <TableCell sx={thSx} align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {orders.map((order) => {
                      const s = orderStatusMap[order.order_status] || { label: order.order_status, color: P.muted };
                      return (
                        <TableRow key={order.id} sx={{ '&:hover': { bgcolor: 'rgba(29,21,48,0.015)' } }}>
                          <TableCell sx={tdSx}>
                            <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: P.text }}>{order.order_number}</Typography>
                            <Typography sx={{ fontSize: '0.62rem', color: P.muted }}>#{order.id}</Typography>
                          </TableCell>
                          <TableCell sx={tdSx}><Typography sx={{ fontSize: '0.78rem', color: P.soft }}>{order.restaurant_name || '--'}</Typography></TableCell>
                          <TableCell sx={tdSx}>
                            <Typography sx={{ fontSize: '0.78rem', color: P.text }}>{order.customer_name || '--'}</Typography>
                            <Typography sx={{ fontSize: '0.65rem', color: P.muted }}>{order.customer_phone}</Typography>
                          </TableCell>
                          <TableCell sx={tdSx} align="right">
                            <Typography sx={{ ...monoNum, fontSize: '0.82rem', color: P.gold }}>{fmtEur(order.total_amount)}</Typography>
                            <Typography sx={{ fontSize: '0.62rem', color: P.muted }}>Livr. {order.delivery_fee} €</Typography>
                          </TableCell>
                          <TableCell sx={tdSx} align="center">
                            <StatusDot color={s.color} label={s.label} />
                          </TableCell>
                          <TableCell sx={tdSx}>
                            <Typography sx={{ fontSize: '0.72rem', color: P.muted }}>
                              {fmtDateTZ(order.created_at, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                            </Typography>
                          </TableCell>
                          <TableCell sx={tdSx} align="center">
                            <Box display="flex" justifyContent="center" gap={0.5}>
                              <IconButton size="small" onClick={() => { setEditOrder(order); setEditStatus(order.order_status); }}
                                sx={{ color: P.soft, '&:hover': { color: P.orange } }}>
                                <EditIcon sx={{ fontSize: 15 }} />
                              </IconButton>
                              <IconButton size="small" onClick={() => navigate(`/restaurant/orders/${order.id}/tracking`)}
                                sx={{ color: P.soft, '&:hover': { color: P.blue } }}>
                                <VisibilityIcon sx={{ fontSize: 15 }} />
                              </IconButton>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {orders.length === 0 && (
                      <TableRow><TableCell colSpan={7} sx={{ ...tdSx, textAlign: 'center', py: 5, color: P.muted }}>Aucune commande trouvee</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            {ordersTotal > 1 && (
              <Box display="flex" justifyContent="center" mt={3}>
                <Pagination count={ordersTotal} page={ordersPage} onChange={(_, v) => setOrdersPage(v)} size="small"
                  sx={{ '& .MuiPaginationItem-root': { color: P.soft, fontSize: '0.72rem' }, '& .Mui-selected': { bgcolor: `${P.purple} !important`, color: 'white !important' } }} />
              </Box>
            )}
          </Box>
        )}

        {/* ════════════════════════════════════════════════════════
            TAB 3 -- RESTAURANTS
        ════════════════════════════════════════════════════════ */}
        {tab === 3 && (
          <Box sx={{ mt: 3 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '3fr auto' }, gap: 1.5, mb: 3, p: 2, border: `0.5px solid ${P.line}`, borderRadius: '4px' }}>
              <TextField fullWidth size="small" placeholder="Nom, adresse, proprietaire..."
                value={restaurantsSearch} onChange={(e) => { setRestaurantsSearch(e.target.value); setRestaurantsPage(1); }}
                InputProps={{ startAdornment: <SearchIcon sx={{ color: P.muted, fontSize: 16, mr: 1 }} /> }}
                sx={inputSx} />
              <IconButton onClick={loadRestaurants} sx={{ color: P.soft, border: `0.5px solid ${P.line}`, borderRadius: '4px', width: 40, height: 40, '&:hover': { borderColor: P.gold, color: P.gold } }}>
                <RefreshIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>

            {restaurantsLoad ? <Box display="flex" justifyContent="center" py={6}><CircularProgress sx={{ color: P.gold }} size={28} thickness={2} /></Box> : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={thSx}>Restaurant</TableCell>
                      <TableCell sx={thSx}>Proprietaire</TableCell>
                      <TableCell sx={thSx}>Cuisine</TableCell>
                      <TableCell sx={thSx} align="right">CA</TableCell>
                      <TableCell sx={thSx} align="center">Cmdes</TableCell>
                      <TableCell sx={thSx} align="center">Note</TableCell>
                      <TableCell sx={thSx} align="center">Statut</TableCell>
                      <TableCell sx={thSx} align="center">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {restaurants.map((resto) => (
                      <TableRow key={resto.id} sx={{ '&:hover': { bgcolor: 'rgba(29,21,48,0.015)' } }}>
                        <TableCell sx={tdSx}>
                          <Box display="flex" alignItems="center" gap={1.5}>
                            <Box sx={{ width: 32, height: 32, borderRadius: '4px', bgcolor: 'rgba(239,68,68,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <RestaurantIcon sx={{ fontSize: 15, color: P.red }} />
                            </Box>
                            <Box>
                              <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: P.text }}>{resto.name}</Typography>
                              <Typography sx={{ fontSize: '0.62rem', color: P.muted, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{resto.address}</Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell sx={tdSx}>
                          <Typography sx={{ fontSize: '0.78rem', color: P.soft }}>{resto.owner_name || '--'}</Typography>
                          <Typography sx={{ fontSize: '0.62rem', color: P.muted }}>{resto.owner_phone}</Typography>
                        </TableCell>
                        <TableCell sx={tdSx}><Typography sx={{ fontSize: '0.78rem', color: P.soft }}>{resto.cuisine_type || '--'}</Typography></TableCell>
                        <TableCell sx={tdSx} align="right"><Typography sx={{ ...monoNum, fontSize: '0.82rem', color: P.gold }}>{fmtEur(resto.total_revenue)}</Typography></TableCell>
                        <TableCell sx={tdSx} align="center"><Typography sx={{ ...monoNum, fontSize: '0.78rem', color: P.text }}>{resto.total_orders || 0}</Typography></TableCell>
                        <TableCell sx={tdSx} align="center">
                          <Typography sx={{ ...monoNum, fontSize: '0.78rem', color: resto.rating > 0 ? P.gold : P.muted }}>
                            {resto.rating > 0 ? resto.rating : '--'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={tdSx} align="center">
                          <StatusDot color={resto.is_open ? P.green : P.red} label={resto.is_open ? 'Ouvert' : 'Ferme'} />
                        </TableCell>
                        <TableCell sx={tdSx} align="center">
                          <Switch size="small" checked={!!resto.is_open} onChange={() => handleToggleRestaurant(resto)}
                            sx={{ '& .Mui-checked': { color: P.green }, '& .Mui-checked + .MuiSwitch-track': { bgcolor: `${P.green} !important` } }} />
                        </TableCell>
                      </TableRow>
                    ))}
                    {restaurants.length === 0 && (
                      <TableRow><TableCell colSpan={8} sx={{ ...tdSx, textAlign: 'center', py: 5, color: P.muted }}>Aucun restaurant trouve</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            {restaurantsTotal > 1 && (
              <Box display="flex" justifyContent="center" mt={3}>
                <Pagination count={restaurantsTotal} page={restaurantsPage} onChange={(_, v) => setRestaurantsPage(v)} size="small"
                  sx={{ '& .MuiPaginationItem-root': { color: P.soft, fontSize: '0.72rem' }, '& .Mui-selected': { bgcolor: `${P.purple} !important`, color: 'white !important' } }} />
              </Box>
            )}
          </Box>
        )}

        {/* ════════════════════════════════════════════════════════
            TAB 4 -- COMMISSIONS
        ════════════════════════════════════════════════════════ */}
        {tab === 4 && (
          <Box sx={{ mt: 3 }}>
            {/* Period selector */}
            <Box display="flex" gap={0} mb={3} sx={{ border: `0.5px solid ${P.line}`, borderRadius: '4px', width: 'fit-content' }}>
              {[['today','Aujourd\'hui'],['week','Semaine'],['month','Mois']].map(([v, l], i) => (
                <Box key={v} onClick={() => setCommissionPeriod(v)}
                  sx={{
                    px: 2, py: 1, cursor: 'pointer', fontSize: '0.72rem', fontWeight: 600,
                    color: commissionPeriod === v ? P.text : P.muted,
                    bgcolor: commissionPeriod === v ? 'rgba(29,21,48,0.06)' : 'transparent',
                    borderRight: i < 2 ? `0.5px solid ${P.line}` : 'none',
                    transition: 'all 0.15s',
                    '&:hover': { color: P.text },
                  }}>
                  {l}
                </Box>
              ))}
            </Box>

            {commissionLoad ? (
              <Box display="flex" justifyContent="center" py={8}><CircularProgress sx={{ color: P.gold }} size={28} thickness={2} /></Box>
            ) : (
              <>
                {/* KPIs */}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: '1fr 1fr 1fr 1fr' }, borderBottom: `0.5px solid ${P.line}`, mb: 4 }}>
                  <Box sx={{ borderRight: { md: `0.5px solid ${P.line}` }, pr: { md: 3 } }}>
                    <Stat label="Volume brut" value={fmtEur(commissionStats?.total_gross)} accent={P.gold} />
                  </Box>
                  <Box sx={{ borderRight: { md: `0.5px solid ${P.line}` }, px: { md: 3 } }}>
                    <Stat label="Commissions Usafiri" value={fmtEur(commissionStats?.total_commission)} accent={P.green} />
                  </Box>
                  <Box sx={{ borderRight: { md: `0.5px solid ${P.line}` }, px: { md: 3 } }}>
                    <Stat label="Net partenaires" value={fmtEur(commissionStats?.total_net)} accent={P.blue} />
                  </Box>
                  <Box sx={{ pl: { md: 3 } }}>
                    <Stat label="Partenaires actifs" value={commissionStats?.active_partners || 0} accent={P.purple} />
                  </Box>
                </Box>

                <Grid container spacing={0}>
                  {/* Commission rates */}
                  <Grid item xs={12} md={4} sx={{ pr: { md: 4 }, borderRight: { md: `0.5px solid ${P.line}` } }}>
                    <Typography sx={{ ...micro, mb: 2 }}>Taux de commission</Typography>
                    {[
                      { label: 'Restauration', icon: <RestaurantIcon sx={{ fontSize: 14 }} />,     color: P.red,    key: 'restaurant' },
                      { label: 'VTC',          icon: <DirectionsCarIcon sx={{ fontSize: 14 }} />,  color: P.purple, key: 'vtc' },
                      { label: 'Location',     icon: <CarRentalIcon sx={{ fontSize: 14 }} />,     color: P.blue,   key: 'car_rental' },
                      { label: 'Covoiturage',  icon: <GroupsIcon sx={{ fontSize: 14 }} />,        color: P.orange, key: 'carpool' },
                      { label: 'Logistique',   icon: <LocalShippingIcon sx={{ fontSize: 14 }} />, color: P.green,  key: 'logistics' },
                      { label: 'Courses',      icon: <ShoppingBagIcon sx={{ fontSize: 14 }} />,   color: '#06b6d4',key: 'courses' },
                    ].map((s) => {
                      const rate = commissionRates.find(r => r.service_type === s.key)?.rate || '--';
                      return (
                        <Box key={s.key} display="flex" justifyContent="space-between" alignItems="center"
                          sx={{ py: 1.3, borderBottom: `0.5px solid ${P.lineFine}` }}>
                          <Box display="flex" alignItems="center" gap={1.2}>
                            <Box sx={{ color: s.color, display: 'flex' }}>{s.icon}</Box>
                            <Typography sx={{ fontSize: '0.78rem', color: P.soft }}>{s.label}</Typography>
                          </Box>
                          <Typography sx={{ ...monoNum, fontSize: '0.82rem', color: s.color }}>
                            {rate !== '--' ? `${rate}%` : '--'}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Grid>

                  {/* Partners table */}
                  <Grid item xs={12} md={8} sx={{ pl: { md: 4 }, mt: { xs: 3, md: 0 } }}>
                    <Typography sx={{ ...micro, mb: 2 }}>Partenaires -- top revenus</Typography>
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={thSx}>Partenaire</TableCell>
                            <TableCell sx={thSx}>Service</TableCell>
                            <TableCell sx={thSx} align="right">Brut</TableCell>
                            <TableCell sx={thSx} align="right">Commission</TableCell>
                            <TableCell sx={thSx} align="right">Net</TableCell>
                            <TableCell sx={thSx} align="center">Stripe</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {commissionPartners.slice(0, 10).map((p, i) => (
                            <TableRow key={i} sx={{ '&:hover': { bgcolor: 'rgba(29,21,48,0.015)' } }}>
                              <TableCell sx={tdSx}>
                                <Box display="flex" alignItems="center" gap={1.2}>
                                  <Box sx={{ width: 28, height: 28, borderRadius: '4px', bgcolor: `${P.purple}12`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: P.purple, fontSize: '0.7rem', fontWeight: 700 }}>
                                    {p.first_name?.[0]}
                                  </Box>
                                  <Box>
                                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: P.text }}>{p.first_name} {p.last_name}</Typography>
                                    <Typography sx={{ fontSize: '0.58rem', color: P.muted }}>{p.email}</Typography>
                                  </Box>
                                </Box>
                              </TableCell>
                              <TableCell sx={tdSx}>
                                <Typography sx={{ fontSize: '0.68rem', color: P.soft }}>{p.order_type}</Typography>
                              </TableCell>
                              <TableCell sx={tdSx} align="right"><Typography sx={{ ...monoNum, fontSize: '0.78rem', color: P.gold }}>{fmtEur(p.gross_amount)}</Typography></TableCell>
                              <TableCell sx={tdSx} align="right"><Typography sx={{ ...monoNum, fontSize: '0.78rem', color: P.green }}>{fmtEur(p.commission_amount)}</Typography></TableCell>
                              <TableCell sx={tdSx} align="right"><Typography sx={{ ...monoNum, fontSize: '0.78rem', color: P.blue }}>{fmtEur(p.net_amount)}</Typography></TableCell>
                              <TableCell sx={tdSx} align="center">
                                <StatusDot color={p.stripe_charges_enabled ? P.green : P.red} label={p.stripe_charges_enabled ? 'Actif' : 'Non'} />
                              </TableCell>
                            </TableRow>
                          ))}
                          {commissionPartners.length === 0 && (
                            <TableRow><TableCell colSpan={6} sx={{ ...tdSx, textAlign: 'center', py: 5, color: P.muted }}>Aucune transaction</TableCell></TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                </Grid>
              </>
            )}
          </Box>
        )}

        {/* ════════════════════════════════════════════════════════
            TAB 5 -- VIREMENTS
        ════════════════════════════════════════════════════════ */}
        {tab === 5 && (
          <Box sx={{ mt: 3 }}>
            {/* Payout KPIs */}
            {payoutTotals && (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, borderBottom: `0.5px solid ${P.line}`, mb: 3 }}>
                {[
                  { label: 'Vires (completed)', value: fmtEur(parseFloat(payoutTotals.total_completed).toFixed(2)), color: P.green },
                  { label: 'En cours',          value: fmtEur(parseFloat(payoutTotals.total_processing).toFixed(2)), color: P.blue },
                  { label: 'Echoues',           value: fmtEur(parseFloat(payoutTotals.total_failed).toFixed(2)),     color: P.red, sub: `${payoutTotals.count_failed} a relancer` },
                ].map((k, i) => (
                  <Box key={i} sx={{ borderRight: i < 2 ? { md: `0.5px solid ${P.line}` } : 'none', pr: i < 2 ? { md: 3 } : 0, pl: i > 0 ? { md: 3 } : 0 }}>
                    <Stat label={k.label} value={k.value} sub={k.sub} accent={k.color} />
                  </Box>
                ))}
              </Box>
            )}

            {/* Filter */}
            <Box display="flex" gap={0} mb={3} alignItems="center" sx={{ border: `0.5px solid ${P.line}`, borderRadius: '4px', width: 'fit-content' }}>
              {[['', 'Tous'], ['completed', 'Completes'], ['processing', 'En cours'], ['failed', 'Echoues']].map(([v, l], i) => (
                <Box key={v} onClick={() => setPayoutFilter(v)}
                  sx={{
                    px: 2, py: 1, cursor: 'pointer', fontSize: '0.72rem', fontWeight: 600,
                    color: payoutFilter === v ? P.text : P.muted,
                    bgcolor: payoutFilter === v ? 'rgba(29,21,48,0.06)' : 'transparent',
                    borderRight: i < 3 ? `0.5px solid ${P.line}` : 'none',
                    transition: 'all 0.15s',
                    '&:hover': { color: P.text },
                  }}>
                  {l}
                </Box>
              ))}
            </Box>

            {/* Table */}
            {payoutsLoad ? (
              <Box display="flex" justifyContent="center" py={6}><CircularProgress sx={{ color: P.gold }} size={28} thickness={2} /></Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={thSx}>Beneficiaire</TableCell>
                      <TableCell sx={thSx}>Role</TableCell>
                      <TableCell sx={thSx}>Commande</TableCell>
                      <TableCell sx={thSx} align="right">Montant</TableCell>
                      <TableCell sx={thSx}>IBAN</TableCell>
                      <TableCell sx={thSx}>Transfer ID</TableCell>
                      <TableCell sx={thSx} align="center">Statut</TableCell>
                      <TableCell sx={thSx} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {payouts.length === 0 ? (
                      <TableRow><TableCell colSpan={8} sx={{ ...tdSx, textAlign: 'center', py: 5, color: P.muted }}>Aucun virement</TableCell></TableRow>
                    ) : payouts.map((p) => {
                      const statusColor = p.status === 'completed' ? P.green : p.status === 'failed' ? P.red : P.blue;
                      const statusLabel = p.status === 'completed' ? 'Vire' : p.status === 'failed' ? 'Echoue' : 'En cours';
                      return (
                        <TableRow key={p.id} sx={{ '&:hover': { bgcolor: 'rgba(29,21,48,0.015)' } }}>
                          <TableCell sx={tdSx}>
                            <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: P.text }}>{p.user_name}</Typography>
                            <Typography sx={{ fontSize: '0.62rem', color: P.muted }}>{p.user_email}</Typography>
                          </TableCell>
                          <TableCell sx={tdSx}>
                            <StatusDot color={p.role === 'restaurateur' ? P.red : P.orange} label={p.role} />
                          </TableCell>
                          <TableCell sx={tdSx}>
                            <Typography sx={{ fontSize: '0.78rem', color: P.soft }}>#{p.order_number}</Typography>
                          </TableCell>
                          <TableCell sx={tdSx} align="right">
                            <Typography sx={{ ...monoNum, fontSize: '0.82rem', color: P.gold }}>{fmtEur(parseFloat(p.amount).toFixed(2))}</Typography>
                          </TableCell>
                          <TableCell sx={tdSx}>
                            <Typography sx={{ ...monoNum, fontSize: '0.68rem', color: P.muted }}>
                              {p.iban ? `${p.iban.slice(0, 6)}...${p.iban.slice(-4)}` : '--'}
                            </Typography>
                          </TableCell>
                          <TableCell sx={tdSx}>
                            <Typography sx={{ ...monoNum, fontSize: '0.68rem', color: P.muted }}>
                              {p.wise_transfer_id || '--'}
                            </Typography>
                          </TableCell>
                          <TableCell sx={tdSx} align="center">
                            <StatusDot color={statusColor} label={statusLabel} />
                            {p.error_message && (
                              <Tooltip title={p.error_message}>
                                <Typography sx={{ fontSize: '0.58rem', color: P.red, cursor: 'help', mt: 0.3 }}>voir detail</Typography>
                              </Tooltip>
                            )}
                          </TableCell>
                          <TableCell sx={tdSx}>
                            {p.status === 'failed' && (
                              <Button size="small"
                                onClick={async () => {
                                  try {
                                    await api.post(`/admin/payouts/${p.id}/retry`);
                                    showSuccess('Virement relance');
                                    loadPayouts();
                                  } catch (e) {
                                    setError(e.response?.data?.message || 'Erreur relance');
                                  }
                                }}
                                sx={{ fontSize: '0.65rem', py: 0.3, px: 1.5, color: P.orange, border: `0.5px solid ${P.orange}`, borderRadius: '4px',
                                  textTransform: 'none', '&:hover': { bgcolor: 'rgba(245,158,11,0.08)' } }}>
                                Relancer
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Info */}
            <Box sx={{ mt: 3, py: 2, px: 2.5, borderLeft: `2px solid ${P.purple}`, bgcolor: 'rgba(85,37,131,0.04)' }}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: P.soft, mb: 0.5 }}>Virements Stripe Connect Express</Typography>
              <Typography sx={{ fontSize: '0.68rem', color: P.muted, lineHeight: 1.6 }}>
                Declenchement automatique apres confirmation de livraison. Le partenaire doit avoir finalise son onboarding Stripe.
                Les virements echoues peuvent etre relances apres connexion du compte Stripe.
              </Typography>
            </Box>
          </Box>
        )}

        {/* ════════════════════════════════════════════════════════
            TAB 6 -- VEHICULES (verification)
           ════════════════════════════════════════════════════════ */}
        {/* ════════════════════════════════════════════════════════
            TAB 8 -- SUPPRESSIONS DE COMPTE
        ════════════════════════════════════════════════════════ */}
        {tab === 8 && (
          <Box sx={{ mt: 3 }}>
            <DeletionRequestsTab P={P} thSx={thSx} tdSx={tdSx} />
          </Box>
        )}

        {/* TAB 9 -- MESSAGERIE (aide Usafiri) */}
        {tab === 9 && (
          <Box sx={{ mt: 3 }}>
            <SupportChatTab P={P} />
          </Box>
        )}

        {tab === 10 && <StripeTab P={P} />}

        {/* ════════════════════════════════════════════════════════
            TAB 7 -- NOTIFICATIONS MARKETING
        ════════════════════════════════════════════════════════ */}
        {tab === 7 && (
          <Box sx={{ mt: 3 }}>
            <Grid container spacing={0}>

              {/* Left — form */}
              <Grid item xs={12} md={6} sx={{ pr: { md: 4 }, borderRight: { md: `0.5px solid ${P.line}` } }}>
                <Typography sx={{ ...micro, mb: 2, color: P.gold }}>Nouvelle campagne</Typography>

                {/* Target */}
                <Typography sx={{ ...micro, mb: 1.5 }}>Audience cible</Typography>
                <Box display="flex" gap={0} mb={3} sx={{ border: `0.5px solid ${P.line}`, borderRadius: '4px', width: 'fit-content' }}>
                  {[['all', 'Tous'], ['active', 'Actifs (30j)'], ['inactive', 'Inactifs']].map(([v, l], i) => (
                    <Box key={v} onClick={() => setPushTarget(v)}
                      sx={{
                        px: 2, py: 1, cursor: 'pointer', fontSize: '0.72rem', fontWeight: 600,
                        color: pushTarget === v ? P.text : P.muted,
                        bgcolor: pushTarget === v ? 'rgba(29,21,48,0.06)' : 'transparent',
                        borderRight: i < 2 ? `0.5px solid ${P.line}` : 'none',
                        transition: 'all 0.15s',
                        '&:hover': { color: P.text },
                      }}>
                      {l}
                    </Box>
                  ))}
                </Box>

                {/* Title */}
                <TextField fullWidth size="small" label="Titre de la notification" value={pushTitle}
                  onChange={(e) => setPushTitle(e.target.value)}
                  inputProps={{ maxLength: 60 }}
                  helperText={`${pushTitle.length}/60`}
                  sx={{ ...inputSx, mb: 2, '& .MuiFormHelperText-root': { color: P.muted, fontSize: '0.62rem' } }} />

                {/* Body */}
                <TextField fullWidth multiline rows={3} size="small" label="Message" value={pushBody}
                  onChange={(e) => setPushBody(e.target.value)}
                  inputProps={{ maxLength: 200 }}
                  helperText={`${pushBody.length}/200`}
                  sx={{ ...inputSx, mb: 2, '& .MuiFormHelperText-root': { color: P.muted, fontSize: '0.62rem' } }} />

                {/* Optional fields */}
                <TextField fullWidth size="small" label="Image URL (optionnel)" value={pushImageUrl}
                  onChange={(e) => setPushImageUrl(e.target.value)}
                  sx={{ ...inputSx, mb: 2 }} />
                <TextField fullWidth size="small" label="Deep link (optionnel, ex: usafiri://wallet)" value={pushDeepLink}
                  onChange={(e) => setPushDeepLink(e.target.value)}
                  sx={{ ...inputSx, mb: 3 }} />

                {/* Result */}
                {pushResult && (
                  <Box sx={{ mb: 2, p: 2, borderLeft: `2px solid ${pushResult.success ? P.green : P.red}`,
                    bgcolor: pushResult.success ? 'rgba(34,197,94,0.04)' : 'rgba(239,68,68,0.04)' }}>
                    {pushResult.success ? (
                      <Typography sx={{ fontSize: '0.78rem', color: P.green, fontWeight: 600 }}>
                        Envoi reussi — {pushResult.sent} / {pushResult.total} appareils atteints
                        {pushResult.failed > 0 && ` (${pushResult.failed} echecs)`}
                      </Typography>
                    ) : (
                      <Typography sx={{ fontSize: '0.78rem', color: P.red, fontWeight: 600 }}>{pushResult.message}</Typography>
                    )}
                  </Box>
                )}

                {/* Send button */}
                <Button
                  disabled={!pushTitle.trim() || !pushBody.trim() || pushLoading}
                  onClick={handleSendPush}
                  startIcon={pushLoading ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : <SendIcon sx={{ fontSize: 15 }} />}
                  sx={{
                    bgcolor: pushTitle.trim() && pushBody.trim() ? P.purple : 'rgba(29,21,48,0.05)',
                    color: pushTitle.trim() && pushBody.trim() ? '#fff' : P.muted,
                    textTransform: 'none', borderRadius: '4px', fontWeight: 600, fontSize: '0.82rem', px: 3, py: 1,
                    '&:hover': { bgcolor: '#7B37B8' },
                    '&.Mui-disabled': { bgcolor: 'rgba(85,37,131,0.2)', color: 'rgba(29,21,48,0.25)' },
                  }}>
                  {pushLoading ? 'Envoi en cours...' : 'Envoyer la notification'}
                </Button>
              </Grid>

              {/* Right — campaign history */}
              <Grid item xs={12} md={6} sx={{ pl: { md: 4 }, mt: { xs: 4, md: 0 } }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                  <Typography sx={{ ...micro }}>Historique des campagnes</Typography>
                  <IconButton onClick={loadCampaigns} size="small"
                    sx={{ color: P.soft, border: `0.5px solid ${P.line}`, borderRadius: '4px', width: 28, height: 28,
                      '&:hover': { borderColor: P.gold, color: P.gold } }}>
                    <RefreshIcon sx={{ fontSize: 13 }} />
                  </IconButton>
                </Box>

                {campaignsLoad ? (
                  <Box display="flex" justifyContent="center" py={6}><CircularProgress sx={{ color: P.gold }} size={24} thickness={2} /></Box>
                ) : campaigns.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 8 }}>
                    <CampaignIcon sx={{ fontSize: 36, color: P.muted, mb: 1 }} />
                    <Typography sx={{ fontSize: '0.78rem', color: P.muted }}>Aucune campagne envoyee</Typography>
                  </Box>
                ) : (
                  campaigns.map((c, i) => (
                    <Box key={i} sx={{ py: 1.8, borderBottom: `0.5px solid ${P.lineFine}` }}>
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                        <Box sx={{ flex: 1, mr: 2 }}>
                          <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: P.text }}>{c.title}</Typography>
                          <Typography sx={{ fontSize: '0.72rem', color: P.soft, mt: 0.3, lineHeight: 1.4 }}>{c.body}</Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                          <Typography sx={{ ...monoNum, fontSize: '0.88rem', color: P.green }}>{c.sent_count}</Typography>
                          <Typography sx={{ fontSize: '0.58rem', color: P.muted }}>envois</Typography>
                        </Box>
                      </Box>
                      <Box display="flex" gap={1.5} mt={0.8} alignItems="center">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {c.target === 'all'
                            ? <PeopleIcon sx={{ fontSize: 11, color: P.muted }} />
                            : c.target === 'active'
                            ? <PeopleIcon sx={{ fontSize: 11, color: P.green }} />
                            : <PersonOffIcon sx={{ fontSize: 11, color: P.orange }} />}
                          <Typography sx={{ fontSize: '0.62rem', color: P.muted }}>
                            {c.target === 'all' ? 'Tous' : c.target === 'active' ? 'Actifs' : 'Inactifs'}
                          </Typography>
                        </Box>
                        <Typography sx={{ fontSize: '0.62rem', color: P.muted }}>
                          {fmtDateTZ(c.sent_at, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                        </Typography>
                      </Box>
                    </Box>
                  ))
                )}
              </Grid>
            </Grid>
          </Box>
        )}

        {tab === 6 && (
          <Box sx={{ mt: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
              <Box>
                <Typography sx={{ ...micro, mb: 0.5, color: P.gold }}>Demandes en attente</Typography>
                <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, color: P.text }}>
                  {pendingVehicles.length} vehicule{pendingVehicles.length !== 1 ? 's' : ''} a verifier
                </Typography>
              </Box>
              <IconButton onClick={loadVehicles} sx={{ color: P.soft, border: `0.5px solid ${P.line}`, borderRadius: '4px', width: 32, height: 32,
                '&:hover': { borderColor: P.gold, color: P.gold } }}>
                <RefreshIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Box>

            {vehiclesLoad ? (
              <Box display="flex" justifyContent="center" py={6}>
                <CircularProgress sx={{ color: P.gold }} size={24} thickness={2} />
              </Box>
            ) : pendingVehicles.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <CarRentalIcon sx={{ fontSize: 40, color: P.muted, mb: 1 }} />
                <Typography sx={{ fontSize: '0.82rem', color: P.soft }}>Aucune demande en attente</Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {pendingVehicles.map((v) => (
                  <Box key={v.carId} sx={{ bgcolor: P.surface, border: `0.5px solid ${P.line}`, borderRadius: '6px', overflow: 'hidden' }}>
                    {/* Vehicle header */}
                    <Box sx={{ px: 2.5, py: 2, borderBottom: `0.5px solid ${P.line}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Box>
                          <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: P.text }}>
                            {v.brand} {v.model} ({v.year})
                          </Typography>
                          <Typography sx={{ fontSize: '0.7rem', color: P.muted }}>
                            {v.vehicleType === 'moto' ? 'Moto' : v.vehicleType === 'bateau' ? 'Bateau' : 'Voiture'} -- {v.category} -- {v.pricePerDay} EUR/jour
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: P.soft }}>
                          {v.owner?.name}
                        </Typography>
                        <Typography sx={{ fontSize: '0.62rem', color: P.muted }}>
                          {v.owner?.email} -- {v.owner?.phone}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Vehicle photo */}
                    {v.image && (
                      <Box sx={{ px: 2.5, pt: 2 }}>
                        <Typography sx={{ ...micro, mb: 1 }}>Photo principale</Typography>
                        <Box sx={{ width: 180, height: 120, borderRadius: '4px', overflow: 'hidden', border: `0.5px solid ${P.line}`, cursor: 'pointer' }}
                          onClick={() => setVehiclePreview(v.image)}>
                          <img src={v.image} alt="vehicule" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        </Box>
                      </Box>
                    )}

                    {/* Documents */}
                    <Box sx={{ px: 2.5, py: 2 }}>
                      <Typography sx={{ ...micro, mb: 1.5 }}>Documents</Typography>
                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr' }, gap: 1.5 }}>
                        {[
                          { label: 'Piece d\'identite', url: v.documents?.identity },
                          { label: 'Carte grise', url: v.documents?.carteGrise },
                          { label: 'Assurance', url: v.documents?.insurance },
                          { label: 'Controle technique', url: v.documents?.controleTechnique, optional: !v.documents?.controleTechniqueRequired },
                        ].map((doc, i) => (
                          <Box key={i} sx={{ py: 1.5, px: 2, border: `0.5px solid ${P.line}`, borderRadius: '4px',
                            bgcolor: doc.url ? 'rgba(34,197,94,0.04)' : 'rgba(239,68,68,0.04)',
                            borderColor: doc.url ? 'rgba(34,197,94,0.2)' : (doc.optional ? P.line : 'rgba(239,68,68,0.2)') }}>
                            <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: P.muted, textTransform: 'uppercase', letterSpacing: '0.08em', mb: 0.5 }}>
                              {doc.label}
                            </Typography>
                            {doc.url ? (
                              <Typography component="a" href={doc.url} target="_blank" rel="noopener noreferrer"
                                sx={{ fontSize: '0.72rem', fontWeight: 600, color: P.blue, textDecoration: 'none',
                                  '&:hover': { textDecoration: 'underline' } }}>
                                Voir le document
                              </Typography>
                            ) : (
                              <Typography sx={{ fontSize: '0.72rem', color: doc.optional ? P.muted : P.red, fontWeight: 600 }}>
                                {doc.optional ? 'Non requis' : 'Manquant'}
                              </Typography>
                            )}
                          </Box>
                        ))}
                      </Box>
                    </Box>

                    {/* Actions */}
                    <Box sx={{ px: 2.5, py: 2, borderTop: `0.5px solid ${P.line}`, display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
                      <Button size="small" onClick={() => { setRejectDialog(v.carId); setRejectReason(''); }}
                        sx={{ fontSize: '0.72rem', py: 0.5, px: 2.5, color: P.red, border: `0.5px solid ${P.red}`, borderRadius: '4px',
                          textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: 'rgba(239,68,68,0.08)' } }}>
                        Refuser
                      </Button>
                      <Button size="small" onClick={() => handleApproveVehicle(v.carId)}
                        sx={{ fontSize: '0.72rem', py: 0.5, px: 2.5, bgcolor: P.green, color: '#fff', borderRadius: '4px',
                          textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: '#1aa34a' } }}>
                        Approuver
                      </Button>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        )}
      </Container>

      {/* ── Dialog: modifier utilisateur ────────────────────────── */}
      <Dialog open={!!editUser} onClose={() => setEditUser(null)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { bgcolor: P.surface, border: `0.5px solid ${P.line}`, borderRadius: '6px' } }}>
        <DialogTitle sx={{ fontWeight: 700, color: P.text, fontSize: '1rem', pb: 1, borderBottom: `0.5px solid ${P.line}` }}>Modifier l'utilisateur</DialogTitle>
        {editUser && (
          <DialogContent sx={{ pt: '20px !important' }}>
            <Box sx={{ p: 2, mb: 2.5, borderLeft: `2px solid ${P.purple}`, bgcolor: 'rgba(85,37,131,0.04)' }}>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: P.text }}>{editUser.first_name} {editUser.last_name}</Typography>
              <Typography sx={{ fontSize: '0.72rem', color: P.muted }}>{editUser.email}</Typography>
            </Box>
            <TextField select fullWidth label="Role" value={editUser.user_type} size="small"
              onChange={(e) => setEditUser({ ...editUser, user_type: e.target.value })} sx={{ ...inputSx, mb: 3 }}>
              <MenuItem value="client">Client</MenuItem>
              <MenuItem value="driver">Chauffeur VTC</MenuItem>
              <MenuItem value="delivery">Livreur</MenuItem>
              <MenuItem value="restaurant_owner">Restaurateur</MenuItem>
              <MenuItem value="admin">Administrateur</MenuItem>
            </TextField>
            <Box display="flex" gap={4}>
              <Box display="flex" alignItems="center" gap={1}>
                <Switch size="small" checked={!!editUser.is_active} onChange={(e) => setEditUser({ ...editUser, is_active: e.target.checked })}
                  sx={{ '& .Mui-checked': { color: P.green }, '& .Mui-checked + .MuiSwitch-track': { bgcolor: `${P.green} !important` } }} />
                <Typography sx={{ fontSize: '0.78rem', color: P.soft }}>Actif</Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1}>
                <Switch size="small" checked={!!editUser.is_verified} onChange={(e) => setEditUser({ ...editUser, is_verified: e.target.checked })}
                  sx={{ '& .Mui-checked': { color: P.blue }, '& .Mui-checked + .MuiSwitch-track': { bgcolor: `${P.blue} !important` } }} />
                <Typography sx={{ fontSize: '0.78rem', color: P.soft }}>Verifie</Typography>
              </Box>
            </Box>
          </DialogContent>
        )}
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1, borderTop: `0.5px solid ${P.line}` }}>
          <Button onClick={() => setEditUser(null)} sx={{ color: P.muted, textTransform: 'none', fontSize: '0.78rem' }}>Annuler</Button>
          <Button onClick={handleSaveUser}
            sx={{ bgcolor: P.text, color: P.bg, textTransform: 'none', borderRadius: '4px', fontWeight: 600, fontSize: '0.78rem', px: 3,
              '&:hover': { bgcolor: P.gold, color: '#fff' } }}>
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog: refuser vehicule ───────────────────────────── */}
      <Dialog open={!!rejectDialog} onClose={() => setRejectDialog(null)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { bgcolor: P.surface, border: `0.5px solid ${P.line}`, borderRadius: '6px' } }}>
        <DialogTitle sx={{ fontWeight: 700, color: P.text, fontSize: '1rem', pb: 1, borderBottom: `0.5px solid ${P.line}` }}>Refuser le vehicule</DialogTitle>
        <DialogContent sx={{ pt: '20px !important' }}>
          <TextField fullWidth multiline rows={3} label="Motif du refus" value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)} size="small" placeholder="Expliquez la raison du refus..."
            sx={inputSx} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1, borderTop: `0.5px solid ${P.line}` }}>
          <Button onClick={() => setRejectDialog(null)} sx={{ color: P.muted, textTransform: 'none', fontSize: '0.78rem' }}>Annuler</Button>
          <Button onClick={handleRejectVehicle} disabled={!rejectReason.trim()}
            sx={{ bgcolor: P.red, color: '#fff', textTransform: 'none', borderRadius: '4px', fontWeight: 600, fontSize: '0.78rem', px: 3,
              '&:hover': { bgcolor: '#dc2626' }, '&.Mui-disabled': { bgcolor: 'rgba(239,68,68,0.3)', color: 'rgba(29,21,48,0.5)' } }}>
            Confirmer le refus
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog: preview image ──────────────────────────────── */}
      <Dialog open={!!vehiclePreview} onClose={() => setVehiclePreview(null)} maxWidth="md"
        PaperProps={{ sx: { bgcolor: 'transparent', boxShadow: 'none' } }}>
        <Box sx={{ position: 'relative' }}>
          <IconButton onClick={() => setVehiclePreview(null)}
            sx={{ position: 'absolute', top: 8, right: 8, bgcolor: 'rgba(0,0,0,0.6)', color: '#fff',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' }, width: 32, height: 32 }}>
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
          {vehiclePreview && <img src={vehiclePreview} alt="preview" style={{ maxWidth: '100%', maxHeight: '80vh', borderRadius: 8 }} />}
        </Box>
      </Dialog>

      {/* ── Dialog: modifier statut commande ────────────────────── */}
      <Dialog open={!!editOrder} onClose={() => setEditOrder(null)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { bgcolor: P.surface, border: `0.5px solid ${P.line}`, borderRadius: '6px' } }}>
        <DialogTitle sx={{ fontWeight: 700, color: P.text, fontSize: '1rem', pb: 1, borderBottom: `0.5px solid ${P.line}` }}>Modifier le statut</DialogTitle>
        {editOrder && (
          <DialogContent sx={{ pt: '20px !important' }}>
            <Box sx={{ p: 2, mb: 2.5, borderLeft: `2px solid ${P.gold}`, bgcolor: 'rgba(253,185,39,0.04)' }}>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: P.text }}>Commande {editOrder.order_number}</Typography>
              <Typography sx={{ fontSize: '0.72rem', color: P.muted }}>{editOrder.restaurant_name} -- {fmtEur(editOrder.total_amount)}</Typography>
            </Box>
            <TextField select fullWidth label="Nouveau statut" value={editStatus} size="small"
              onChange={(e) => setEditStatus(e.target.value)} sx={inputSx}>
              {Object.entries(orderStatusMap).map(([k, v]) => (
                <MenuItem key={k} value={k}>
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: v.color }} />
                    {v.label}
                  </Box>
                </MenuItem>
              ))}
            </TextField>
          </DialogContent>
        )}
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1, borderTop: `0.5px solid ${P.line}` }}>
          <Button onClick={() => setEditOrder(null)} sx={{ color: P.muted, textTransform: 'none', fontSize: '0.78rem' }}>Annuler</Button>
          <Button onClick={handleUpdateOrder}
            sx={{ bgcolor: P.text, color: P.bg, textTransform: 'none', borderRadius: '4px', fontWeight: 600, fontSize: '0.78rem', px: 3,
              '&:hover': { bgcolor: P.gold, color: '#fff' } }}>
            Mettre a jour
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
    </ThemeProvider>
  );
};

export default AdminDashboard;
